import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, RouterStateSnapshot} from '@angular/router';
import {Observable, BehaviorSubject, ReplaySubject, Subject} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {AccountManagementProviderService} from '../account-management-provider.service';
import {defaultApiURL, modulesSettings} from '../../../../settings';
import {ModelSchema, Structures} from 'octopus-model';
import {CommunicationCenterService} from '@modules/communication-center';
import {Field} from 'shared/fieldsOptions';
import {AuthenticationService} from '@modules/authentication';
import {UserDataEntity} from '@modules/authentication/core/models/user-data-entity.type';
import {DataEntity, OctopusConnectService} from 'octopus-connect';
import {tap} from 'rxjs/operators';

const settingsStructure: ModelSchema = new ModelSchema({
    allowedExtensions: Structures.array(['jpg', 'jpeg', 'png']),
    allowedMaxSize: Structures.number(10),
    canUnlinkParent: Structures.array([]), // Should be roles
    canLinkChild: Structures.array([]), // Should be roles
    canEditAvatar: Structures.array(['learner', 'trainer', 'manager', 'administrator']),
    canEditYouAre: Structures.boolean(true),
    canSelfDelete: Structures.array([]),
    canJoinGroup: Structures.array(['learner', 'trainer', 'manager', 'administrator']),
    fields: Structures.object({
        default: ['label', 'email', 'password', 'you_are', 'find_us', 'newsletter', 'picture']
    }),
    editableFields: Structures.object({
        default: ['label', 'email', 'password', 'you_are', 'find_us', 'newsletter', 'picture', 'checkNotifMail']
    }),
    youAreValues: Structures.array(['teacher', 'parent', 'teen', 'district', 'other']),
    findUsValues: Structures.array(['advertisement', 'mouth', 'research', 'tradeshow', 'training', 'program', 'other']),
    userIsAdultOrMinorOption: Structures.boolean(true),
    hideEditSSO: Structures.boolean(false),
    showJoinSchool: Structures.boolean(false), // todo see how to manage right by level with arthur stuff
    showJoinGroup: Structures.boolean(false),
    feedbackMaxFileSize: Structures.string('100'), // max size of file to join in feedback,
    feedbackMaxFileToSend: Structures.string('10'), // number max of file to join
    feedbackUseInputFieldForBug: Structures.boolean(false), // use input field instead of generating text format in msg zone,
    idSubjectToOpenBugInputs: Structures.string(''), // id to launch other input for bug if feedbackUseInputFieldForBug
    isEducationalLevelSelectionHiddenIfUserIsTeacher: Structures.boolean(false),
    allowedMultiplesGroups: Structures.boolean(false),
    useleaveGroupEvent: Structures.boolean(false),
    useSpecificDateForGarLicence: Structures.boolean(false),
    emailRequiredConditionnal: Structures.boolean(false),
});

const URL_FILE_UPLOAD = defaultApiURL + 'api/file-upload';

@Injectable()
export class ProfileService {
    public userInformation: UserDataEntity | null;
    public userInformationOnChanged = new BehaviorSubject<UserDataEntity>(null);
    public settings: { [key: string]: any };
    private fileOnChanged = new BehaviorSubject<File>(null);
    private file: File;

    constructor(
        private http: HttpClient,
        private communicationService: CommunicationCenterService,
        private accountManagementProvider: AccountManagementProviderService,
        private authenticationService: AuthenticationService,
        private octopusConnectService: OctopusConnectService
    ) {
        this.settings = settingsStructure.filterModel(modulesSettings.accountManagement);
        this.accountManagementProvider.getCurrentUserObs.subscribe(() => {
            this.userInformation = this.accountManagementProvider.loggedUser;
            this.userInformationOnChanged.next(this.userInformation);
        });
    }

    private _selectedMode = false;

    get selectedMode(): boolean {
        return this._selectedMode;
    }

    set editMode(mode: boolean) {
        this._selectedMode = mode || false;
    }

    public set editFile(data: File) {
        this.file = data || null;
        this.fileOnChanged.next(data);
    }

    get accessToken(): string {
        return this.accountManagementProvider.userAccessToken;
    }

    /**
     * Resolve
     * @param {ActivatedRouteSnapshot} route
     * @param {RouterStateSnapshot} state
     * @returns {Observable<any> | Promise<any> | any}
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<void> {

        return new Promise((resolve) => {

            this.accountManagementProvider.getCurrentUserObs.subscribe(() => {
                this.userInformation = this.accountManagementProvider.loggedUser;
                this.userInformationOnChanged.next(this.userInformation);
                resolve();

            });

        });
    }

    public getEditableFields(): Field[] {
        let fields = this.settings.editableFields[this.authenticationService.accessLevel];
        if (fields === null || fields === undefined) {
            fields = this.settings.editableFields['default'] || [];
        }

        return fields.map((field: string) => new Field(field));
    }

    /**
     * Save user data
     * @param user
     * @return Subject with true if success, false if error
     */
    public editUser(user): Subject<boolean> {
        return this.file ? this.uploadFile(user, this.file) : this.editUserProfile(user);
    }

    editValidateMail(userData): void {
        this.accountManagementProvider.loggedUser = userData;
        this.accountManagementProvider.editUserValidateEmail();
    }

    /**
     * Call Authentication service for logout current user
     * @returns {ReplaySubject} emitted while logout is complete
     */
    public deleteCurrentProfile(): ReplaySubject<void> {
        const onDeleteComplete = new ReplaySubject<void>(1);

        this.accountManagementProvider
            .deleteCurrentLoggedUser()
            .subscribe((successfullyDeletion) => {
                if (successfullyDeletion) {
                    this.communicationService
                        .getRoom('authentication')
                        .next('do-logout', () => onDeleteComplete.next());
                } else {
                    throw new Error('Unable to delete currently logged user');
                }
            });

        return onDeleteComplete;
    }

    public getFindUsValues(): any[] {
        return this.settings.findUsValues;
    }

    public getYouAreValues(): any[] {
        return this.settings.youAreValues;
    }

    public getCanSelfDelete(): any[] {
        return this.settings.canSelfDelete;
    }

    public getAllowedExtensions(): string[] {
        return this.settings.allowedExtensions;
    }

    public getAllowedMaxSize(): number {
        return this.settings.allowedMaxSize;
    }

    public getCanEditAvatar(): boolean {
        return this.settings.canEditAvatar.includes(this.authenticationService.accessLevel);
    }

    public getCanEditYouAre(): boolean {
        return this.settings.canEditYouAre;
    }

    public getCanJoinGroup(): boolean {
        return this.settings.canJoinGroup.includes(this.authenticationService.accessLevel) && !this.authenticationService.isGAR();
    }

    /**
     * Get the current associated parent label or null if there are no parent.
     */
    public getLinkedParentLabelOrNull(): string | null {
        return !!this.userInformation ? this.userInformation.get('parent') : null;
    }

    /**
     * Delete the current relation between the current logged user and his parent (should be a trainer)
     */
    public unlinkParent(): Observable<{}> {
        return this.octopusConnectService.createEntity('users-link', {action: 'unlink'}).pipe(
            tap(() => this.userInformation.set('parent', null))
        );
    }

    /**
     * Set the child account as a children of current user account.
     *
     * @remarks return nothing if the users is found or not
     * @param childIdentifier
     */
    public linkChild(childIdentifier: string): Observable<DataEntity> {
        return this.octopusConnectService.createEntity('users-link', {child: childIdentifier}).pipe(
            tap(() => this.communicationService.getRoom('groups-management').next('refreshLearners', undefined))
        );
    }

    /**
     * Return true if user is authorized to unlink parent account
     */
    public getCanUnlinkParent(): boolean {
        return (<string[]>this.settings.canUnlinkParent).includes(this.authenticationService.accessLevel)
            && !!this.getLinkedParentLabelOrNull();
    }

    /**
     * Return true if user is authorized to link a child account
     */
    public getCanLinkChild(): boolean {
        return (<string[]>this.settings.canLinkChild).includes(this.authenticationService.accessLevel);
    }

    private editUserProfile(user): Subject<boolean> {
        const editCompleteSubject = new Subject<boolean>();

        if (user.password && user.password === '') {
            delete user.password;
        }

        this.accountManagementProvider.editUser(user, (isSuccess: boolean) => {
            if (isSuccess) {
                this.editMode = false;
                this.userInformationOnChanged.next(this.accountManagementProvider.loggedUser);
            }

            editCompleteSubject.next(isSuccess);
        });

        return editCompleteSubject;
    }

    /** @deprecated use MediaService.createMedia instead */
    private uploadFile(user, fileToUpload: File): Subject<boolean> {
        const editCompleteSubject = new Subject<boolean>();


        const formData = new FormData();
        formData.append('file', fileToUpload);

        this.http
            .post<any>(URL_FILE_UPLOAD, formData, {headers: {'access-token': this.accessToken}})
            .subscribe((res) => {
                user.picture = res.data[0][0].id;
                this.editFile = null;
                this.editUserProfile(user)
                    .subscribe(isSuccess => editCompleteSubject.next(isSuccess));
            });

        return editCompleteSubject;
    }

    public getAllowedMultiplesGroups(): boolean {
        return this.settings.allowedMultiplesGroups;
    }

    public getUseleaveGroupEvent(): boolean {
        return this.settings.useleaveGroupEvent;
    }

    public getUseSpecificDateForGarLicence(): boolean {
        return this.settings.useSpecificDateForGarLicence;
    }

    public getEmailRequiredConditionnal(): boolean {
        return this.settings.emailRequiredConditionnal;
    }


}
